import { useCallback, useEffect, useState } from "react";
import Loading from "../../components/loading/Loading";
import "./index.css";
import { PrimaryButton } from "../../components/primary/PrimaryButton";
import { Selector } from "../../components/selector/Selector";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { roles } from "../../constants/roles";
import { capitalized } from "../../utils";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/modal/Modal";
import {
  getClientsByOrganizations,
  getUsers,
} from "../../services/UsersService";
import Register from "../../pages/logged/register/Register";
import _ from "lodash";
import { FaArrowLeft } from "react-icons/fa";
import { getStatus } from "../../pages/logged/UserSites";

const TypeOfClient = {
  prospecto: "Prospecto",
  clientePotencial: "Cliente Potencial",
  activo: "Activo",
};

const TypeOfProgress = {
  registro: "Registro del usuario",
  terreno: "Selectión de tererreno",
  contrato: "Contrato",
  apartado: "Apartado",
  envioDocumentos: "Envio de documentos",
  recepcionDocumentos: "Recepción de documentos",
  aprobacionDocumentos: "Aprobación de documentos",
  pagoInicial: "Pago inicial",
  pagosTerreno: "Pagos del terreno",
};

const TYPE_OF_CLIENT = [
  {
    value: TypeOfClient.prospecto,
    label: TypeOfClient.prospecto,
  },
  {
    value: TypeOfClient.clientePotencial,
    label: TypeOfClient.clientePotencial,
  },
  {
    value: TypeOfClient.activo,
    label: TypeOfClient.activo,
  },
];

const STATUS = [
  {
    value: 1,
    label: TypeOfProgress.registro,
  },
  {
    value: 2,
    label: TypeOfProgress.terreno,
  },
  {
    value: 3,
    label: TypeOfProgress.apartado,
  },
  {
    value: 4,
    label: TypeOfProgress.envioDocumentos,
  },
  {
    value: 5,
    label: TypeOfProgress.recepcionDocumentos,
  },
  {
    value: 6,
    label: TypeOfProgress.aprobacionDocumentos,
  },
  {
    value: 7,
    label: TypeOfProgress.contrato,
  },
  {
    value: 8,
    label: TypeOfProgress.pagoInicial,
  },
  {
    value: 9,
    label: TypeOfProgress.pagosTerreno,
  },
];

const heightRegistrationForm = (type, role) => {
  if (type === "cliente") {
    return "690px";
  }
  if (type === "organizacion") {
    return "730px";
  }
  if (type === "vendedor") {
    return "615px";
  }
  return role === roles.SELLER ? "690px" : "615px";
};

export const initializeRoles = [
  { value: "all", label: "Todos" },
  { value: roles.ADMIN, label: capitalized(roles.ADMIN) },
  { value: roles.CLIENT, label: capitalized(roles.CLIENT) },
  { value: roles.COORDINATOR, label: capitalized(roles.COORDINATOR) },
  { value: roles.ORGANIZATION, label: capitalized(roles.ORGANIZATION) },
  { value: roles.SELLER, label: capitalized(roles.SELLER) },
];

const ClientList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchByRol, setSearchByRol] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [showTableUser, setShowTableUsers] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [type, setType] = useState("");
  //TODO: Load client list
  const [clientList, setClientList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedTypeOfClient, setSelectedTypeOfClient] = useState(null);

  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.roles?.[0]?.name?.toLowerCase();

  const adapterStep = useCallback((step) => {
    if (step === null) {
      return 2;
    }

    if (step === 4) {
      return step;
    }

    if (step === 6) {
      return 7;
    }

    if (step === 7) {
      return 7.2;
    }

    return step;
  }, []);

  /**
   * Get user list in the page selected
   */
  const getUserList = useCallback(async (page, download = undefined) => {
    setLoading(true);
    const theUsers = await getUsers(page, download);
    setLoading(false);
    if (!download) {
      if (theUsers?.data?.data) {
        theUsers?.data?.data.forEach((theUser) => {
          theUser.show = false;
        });
        setUsers(theUsers?.data?.data);
        setMeta(theUsers?.data?.meta);
      }
    }
  }, []);

  /**
   * Handle add user to show form
   */
  const handleAdd = useCallback(() => {
    setShowForm(true);
  }, []);

  /**
   * Handle close modal and reload users list
   */
  const handleCloseModal = useCallback(() => {
    getUserList(1);
    setShowForm(false);
    setShowTableUsers(false);
    setUserToEdit(null);
  }, [getUserList]);

  /**
   * Handle success in form
   */
  const handleSuccess = useCallback(() => {
    getUserList(1);
    handleCloseModal();
  }, [getUserList, handleCloseModal]);

  const handleRoleSelected = useCallback((e) => {
    const roleSelected = e.target.value;
    setSearchByRol(roleSelected);
  }, []);

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const filterByTypeOfClient = (client) => {
    const step = adapterStep(client.step);

    if (selectedTypeOfClient) {
      if (selectedTypeOfClient === TypeOfClient.activo) {
        return step >= 7;
      }
      if (selectedTypeOfClient === TypeOfClient.clientePotencial) {
        return step >= 3 && step <= 6;
      }
      if (selectedTypeOfClient === TypeOfClient.prospecto) {
        return step >= 1 && step <= 2;
      }
    }
    return true;
  };

  const filterByStatus = (client) => {
    return !selectedStatus || adapterStep(client.step) === selectedStatus;
  };

  const filteredClients = clientList
    .filter(filterByTypeOfClient)
    .filter(filterByStatus);

  useEffect(() => {
    setLoading(true);
    getClientsByOrganizations({ adminId: user.user.id })
      .then((response) => {
        const clients = response?.data.clients || [];
        setClientList(clients);
      })
      .catch((error) => {
        console.error("Error al obtener los clientes:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <div className="RegisterList container mt-10">
      {loading && <Loading />}
      <div className="flex flex-col justify-center lg:flex-row lg:justify-between items-center mb-10">
        <div className="flex flex-row items-center">
          <PrimaryButton
            className="mr-5 shadow-md"
            style={{
              right: 10,
              width: 50,
              justifyItems: "center",
              borderRadius: ".2rem",
            }}
            onClick={goBack}
          >
            <FaArrowLeft size={15} />
          </PrimaryButton>
          <h1 className="title mr-4">{t("adminSections.users")}</h1>
          <div className="w-full lg:w-1/6 ml-10" style={{ width: "200px" }}>
            <Selector
              label=""
              name="type_of_client"
              defaultValue={TYPE_OF_CLIENT[0]}
              options={TYPE_OF_CLIENT}
              onChange={(value) => {
                setSelectedTypeOfClient(value?.value);
                setSelectedStatus(null);
              }}
              isNewVersion
            />
          </div>
          <div className="w-full lg:w-1/6 ml-10" style={{ width: "200px" }}>
            <Selector
              label=""
              name="status"
              defaultValue={STATUS[0]}
              options={STATUS}
              onChange={(value) => {
                setSelectedStatus(value?.value);
                setSelectedTypeOfClient(null);
              }}
              isNewVersion
            />
          </div>
        </div>
        <PrimaryButton className="shadow max-lg:mt-10 mt-0" onClick={handleAdd}>
          {t("registerPage.add")}
        </PrimaryButton>
      </div>

      <div className="w-full">
        <div className="hidden lg:block overflow-hidden rounded-lg shadow-lg mt-5">
          <table className="w-full  table-auto">
            <thead className="text-black bg-header">
              <tr>
                <td className="p-2">Tipo</td>
                <td className="p-2">Nombre</td>
                <td className="p-2">Apellido</td>
                <td className="p-2">Telefono</td>
                <td className="p-2">Email</td>
                <td className="p-2">Estatus</td>
              </tr>
            </thead>
            <tbody className="text-cell ">
              {_.map(filteredClients, (theClient, index) => (
                <tr
                  className={`border-b border-white text-sm ${
                    index % 2 === 0 ? "bg-white" : "bg-header"
                  }`}
                  key={theClient.id}
                >
                  <td className="p-2 text-primary">{theClient.id}</td>
                  <td className="p-2 text-black-50">{theClient.name}</td>
                  <td className="p-2 text-black-50">{theClient.last_name}</td>
                  <td className="p-2 text-black-50">
                    ({theClient.country_code}) {theClient.phone}
                  </td>
                  <td className="p-2 text-black-50">{theClient.email}</td>
                  <td>{getStatus(adapterStep(theClient.step)) || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <Modal
          onCloseModal={handleCloseModal}
          type="medium"
          title={userToEdit ? t("registerPage.edit") : t("registerPage.add")}
          style={{
            width: "600px",
            minHeight: heightRegistrationForm(type, role),
          }}
        >
          <Register
            userToEdit={userToEdit}
            onSuccess={handleSuccess}
            setType={setType}
            type={type}
          />
        </Modal>
      )}
    </div>
  );
};

export default ClientList;
